import React from 'react';
import styled from 'styled-components';
import { DesignPalette } from '../data/designTemplates';
import { PitchSlide } from '../data/slides';
import { headingFont } from '../styles/fontStyles';
import { pitchTheme } from '../styles/theme';

/** Shared aspect ratio for list-card slide previews. */
export const SLIDE_THUMB_ASPECT_RATIO = '920 / 420';

const ThumbCard = styled.div<{ $palette: DesignPalette }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${SLIDE_THUMB_ASPECT_RATIO};
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid ${({ $palette }) => $palette.border};
  background: ${({ $palette }) => $palette.cream};
`;

const ThumbGradient = styled.div<{ $palette: DesignPalette; $side: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 46%;
  background: ${({ $palette }) => $palette.gradient};
  left: ${({ $side }) => ($side === 'left' ? 0 : 'auto')};
  right: ${({ $side }) => ($side === 'right' ? 0 : 'auto')};
`;

const ThumbContent = styled.div<{ $imageOnRight: boolean }>`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  pointer-events: none;
`;

const TextPanel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6%;
  padding: 10% 8%;
  min-width: 0;
`;

const Title = styled.h3<{ $palette: DesignPalette }>`
  margin: 0;
  ${headingFont}
  font-size: clamp(0.78rem, 3.5vw, 1rem);
  line-height: 1.25;
  letter-spacing: -0.02em;
  color: ${({ $palette }) => $palette.text};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const HighlightList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4%;
`;

const HighlightItem = styled.li<{ $palette: DesignPalette }>`
  position: relative;
  padding-left: 10%;
  font-family: ${pitchTheme.fontContent};
  font-size: clamp(0.625rem, 2.5vw, 0.78rem);
  font-weight: 500;
  line-height: 1.35;
  color: ${({ $palette }) => $palette.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.55em;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${({ $palette }) => $palette.blue};
    transform: translateY(-50%);
  }
`;

const ImagePanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12% 10%;
`;

const PhotoFrame = styled.div`
  width: 72%;
  padding: 4%;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
`;

const Photo = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
`;

const TabBadge = styled.span`
  position: absolute;
  top: 8%;
  right: 8%;
  z-index: 1;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fff;
  background: rgba(5, 21, 84, 0.35);
  border-radius: 999px;
  padding: 3px 8px;
`;

interface SlideThumbnailProps {
  slide: PitchSlide;
  palette: DesignPalette;
  /** Matches carousel alternating layout; defaults to image on the right. */
  imageOnRight?: boolean;
}

const SlideThumbnail: React.FC<SlideThumbnailProps> = ({
  slide,
  palette,
  imageOnRight = true,
}) => {
  const gradientSide = imageOnRight ? 'right' : 'left';

  return (
    <ThumbCard $palette={palette} aria-hidden>
      <ThumbGradient $palette={palette} $side={gradientSide} />
      <ThumbContent $imageOnRight={imageOnRight}>
        <TextPanel style={{ order: imageOnRight ? 1 : 2 }}>
          <Title $palette={palette}>{slide.title}</Title>
          <HighlightList>
            {slide.highlights.slice(0, 2).map((point) => (
              <HighlightItem key={point} $palette={palette}>
                {point}
              </HighlightItem>
            ))}
          </HighlightList>
        </TextPanel>
        <ImagePanel style={{ order: imageOnRight ? 2 : 1 }}>
          <PhotoFrame>
            <Photo src={slide.imageUrl} alt="" loading="lazy" />
          </PhotoFrame>
        </ImagePanel>
      </ThumbContent>
      {slide.tabLabel && <TabBadge>{slide.tabLabel}</TabBadge>}
    </ThumbCard>
  );
};

export default SlideThumbnail;
